import { Card, CardBody, CardHeader, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner, TableCell, TableRow } from "@nextui-org/react";
import { useEffect, useRef, useState } from "react";
import FilterReactSelect from "../../../../elements/FilterReactSelect";
import { getFetcher, getGeoJSON, MAP_BOX_API } from "../../../../../services/api";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { formatNumber } from "../../../../../utils/formatter.utils";
import mapboxgl from "mapbox-gl";
import ClientTablePagination from "../../../../elements/ClientTablePagination";

export default function B3DistributionMap() {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [chemicals, setChemicals] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const selectedYearRef = useRef(selectedYear);
  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year, label: year.toString() };
  });
  const loadGeoJSON = () => {
    setIsLoading(true);

    getGeoJSON(`/api/dashboard/admin/sebaran-b3?year=${selectedYear}`)
      .then((data) => {
        if (data && map.current) {
          if (map.current.getSource("provinces")) {
            map.current.getSource("provinces").setData(data?.data);
          } else {
            if (map.current.getLayer("provinces-layer")) {
              map.current.removeLayer("provinces-layer");
            }
            if (map.current.getSource("provinces")) {
              map.current.removeSource("provinces");
            }

            map.current.addSource("provinces", {
              type: "geojson",
              data: data?.data,
            });

            map.current.addLayer({
              id: "provinces-layer",
              type: "circle",
              source: "provinces",
              paint: {
                "circle-radius": [
                  "interpolate",
                  ["linear"],
                  ["get", "circle_radius"],
                  10,
                  20, // Jika bahan_b3_count = 0, radius = 10
                  20,
                  30, // Jika bahan_b3_count = 250, radius = 20
                  30,
                  40, // Jika bahan_b3_count = 500, radius = 30
                  40,
                  50, // Jika bahan_b3_count = 1000, radius = 40
                ],
                "circle-color": [
                  "interpolate",
                  ["linear"],
                  ["get", "circle_radius"],
                  // Ramp biru design system (--b-300 s.d. --b-900): satu hue, terang
                  // ke gelap. Menggantikan ramp hijau-kuning-merah lama yang urutan
                  // terangnya patah di awal - hijau (nilai terendah) justru lebih
                  // gelap daripada kuning di atasnya.
                  10,
                  "#77B1EE", // b-300
                  20,
                  "#4596ED", // b-400
                  30,
                  "#147DEF", // b-500
                  40,
                  "#0E66C4", // b-600
                  60,
                  "#0E66C4", // b-600
                  80,
                  "#0B529D", // b-700
                  100,
                  "#083E77", // b-800
                  2000,
                  "#062D56", // b-900
                ],
                "circle-opacity": 0.8,
              },
            });

            map.current.addLayer({
              id: "provinces-label",
              type: "symbol",
              source: "provinces",
              layout: {
                "text-field": ["get", "bahan_b3_count"], // Tampilkan jumlah bahan B3
                "text-size": 12, // Ukuran teks
                "text-offset": [0, 0], // Teks di tengah lingkaran
                "text-anchor": "center",
              },
              paint: {
                "text-color": "#ffffff", // Warna teks putih
                "text-halo-color": "#000000", // Warna halo hitam untuk kontras
                "text-halo-width": 1,
              },
            });
          }
        } else {
          toast.error("No data available for GeoJSON.");
        }
      })
      .catch((error) => console.error("Error fetching GeoJSON:", error))
      .finally(() => setIsLoading(false));
  };
  const initializeMap = () => {
    if (map.current) {
      map.current.getSource("provinces")?.setData(null); // Kosongkan data sebelum memuat ulang
    }

    mapboxgl.accessToken = MAP_BOX_API;

    if (!map.current) {
      // Buat instance Mapbox hanya jika belum ada
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        center: [117.015867, -0.574469], // Koordinat Indonesia
        zoom: 5,
      });

      map.current.on("load", () => {
        loadGeoJSON();
      });

      // Tambahkan event untuk klik marker
      map.current.on("click", "provinces-layer", (e) => {
        if (e.features && e.features.length > 0) {
          const provinceId = e.features[0].properties?.province_id; // Validasi properti ID
          console.log(provinceId);
          if (provinceId) {
            openModal(provinceId);
          }
        }
      });

      map.current.on("mouseenter", "provinces-layer", () => {
        map.current.getCanvas().style.cursor = "pointer";
      });

      map.current.on("mouseleave", "provinces-layer", () => {
        map.current.getCanvas().style.cursor = "";
      });
    } else {
      loadGeoJSON();
    }
  };
  useEffect(() => {
    selectedYearRef.current = selectedYear;
  }, [selectedYear]);
  const openModal = async (provinceId) => {
    try {
      const currentYear = selectedYearRef.current; // Dapatkan nilai terbaru
      const detail = await getFetcher(`/api/dashboard/admin/sebaran-provinsi?year=${currentYear}&provinceId=${provinceId}`); // Sesuaikan endpoint API Anda
      setSelectedProvince({
        provinceId: provinceId,
        name: detail?.province_name,
        year: detail?.year,
        longitude: detail?.province_latitude,
        latitude: detail?.province_longitude,
      });
      setChemicals(detail?.bahanB3); // Data bahan kimia (array)
      setModalVisible(true); // Tampilkan modal
    } catch (error) {
      console.error("Error fetching province details:", error);
    }
  };

  const header = ["Nama Bahan Kimia", "Jumlah Pembelian Lokal B3 (Kg)", "Jumlah Pembelian Impor B3 (Kg)", "Jumlah Produksi B3 (Kg)", "Jumlah Distribusi Masuk B3 (Kg)", "Jumlah Penggunaan B3 (Kg)", "Jumlah Distribusi Keluar B3 (Kg)"];

  const content = (item) => (
    <TableRow key={item?.id ?? uuidv4()}>
      <TableCell>{item?.nama_bahan_kimia ?? ""}</TableCell>
      <TableCell className="text-success">+ {formatNumber(item?.pembelian_lokal)}</TableCell>
      <TableCell className="text-success">+ {formatNumber(item?.pembelian_impor)}</TableCell>
      <TableCell className="text-success">+ {formatNumber(item?.produksi)}</TableCell>
      <TableCell className="text-success">+ {formatNumber(item?.distribusi_masuk)}</TableCell>
      <TableCell className="text-danger">- {formatNumber(item?.penggunaan)}</TableCell>
      <TableCell className="text-danger">- {formatNumber(item?.distribusi_keluar)}</TableCell>
    </TableRow>
  );

  useEffect(() => {
    initializeMap();
  }, [selectedYear]);

  return (
    <>
      <Card radius="sm">
        <CardHeader className="w-full flex justify-end">
          <div className="w-1/4">
            <FilterReactSelect defaultValue={{ value: selectedYear, label: selectedYear.toString() }} placeholder="Pilih Tahun" options={yearOptions} setValue={(option) => setSelectedYear(option.value)} menuPosition="absolute" />
          </div>
        </CardHeader>
        <CardBody>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-surface-3 bg-opacity-50 z-20">
              <Spinner size="lg" color="primary" />
            </div>
          )}
          <div ref={mapContainer} className="w-full h-[600px]" style={{ position: "relative" }} />
        </CardBody>
      </Card>
      <Modal size="5xl" isOpen={modalVisible} onClose={() => setModalVisible(false)}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Detail Provinsi {selectedProvince?.name || "Tidak tersedia"}</ModalHeader>
              <ModalBody className={"flex flex-col gap-1"}>
                <p>
                  <strong>Coordinates: </strong> {selectedProvince?.longitude || "Tidak tersedia"}, {selectedProvince?.latitude || "Tidak tersedia"}
                </p>
                <div className="flex w-full justify-center">
                  <ClientTablePagination header={header} data={chemicals} content={content} />
                </div>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
